<template>
  <div class="core-shareholder-container">
    <!-- 和股东名册页面一致的标题样式 -->
    <h2>核心股东配置</h2>
    <el-card shadow="hover" class="card-container">
      <!-- 顶部操作栏 -->
      <div class="top-operation">
        <el-button type="primary" @click="openAddDialog">添加核心股东</el-button>
        <el-button type="danger" @click="handleBatchDelete" :disabled="selectedIds.length === 0">批量删除</el-button>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="shareholderList"
        border
        stripe
        style="margin-top: 16px"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="core_id" label="ID" width="110" />
        <el-table-column prop="name" label="核心股东名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="group_label" label="标签" width="90">
          <template #default="{ row }">
            <span v-if="row.group_label" style="color: #909399">{{ row.group_label }}</span>
            <span v-else style="color: #c0c4cc">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="is_front_shareholder" label="前台账号" width="90">
          <template #default="{ row }">
            <el-tag :type="row.is_front_shareholder ? 'success' : 'info'" style="font-size: 12px">
              {{ row.is_front_shareholder ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="front_account" label="证券号码" min-width="120" show-overflow-tooltip />
        <el-table-column prop="create_time" label="创建时间" width="160" />
        <el-table-column prop="update_time" label="更新时间" width="160" />
        <el-table-column label="操作" min-width="120">
          <template #default="{ row }">
            <el-button size="small" type="primary" @click="openEditDialog(row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(row.core_id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空数据提示 -->
      <div v-if="shareholderList.length === 0" class="empty-data">
        <el-empty description="暂无核心股东数据，请点击添加按钮创建" />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="500px"
      @close="resetForm"
    >
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="140px">
        <el-form-item v-if="isEdit" label="核心股东ID">
          <span style="color: #909399">{{ formData.core_id }}</span>
        </el-form-item>
        <el-form-item label="核心股东名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入核心股东名称" />
        </el-form-item>
        <el-form-item label="股东标签" prop="group_label">
          <el-input v-model="formData.group_label" maxlength="5" placeholder="如：对手盘、xx系（最多5字，选填）" />
        </el-form-item>
        <el-form-item label="是否有前台股票账号">
          <el-radio-group v-model="formData.is_front_shareholder">
            <el-radio :label="true">是</el-radio>
            <el-radio :label="false">否</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="证券号码" v-if="formData.is_front_shareholder">
          <el-input v-model="formData.front_account" placeholder="请输入证券号码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { buildFetchApiHeaders } from '../config/instanceDefaults';

// 数据列表
const shareholderList = ref([]);
// 选中的ID列表
const selectedIds = ref([]);
// 弹窗状态
const dialogVisible = ref(false);
const isEdit = ref(false);
const dialogTitle = ref('');
// 表单数据
const formData = reactive({
  core_id: '',
  name: '',
  group_label: '',
  is_front_shareholder: false,
  front_account: ''
});
// 表单验证规则
const formRules = reactive({
  core_id: [
    { required: true, message: '请输入核心股东ID', trigger: 'blur' }
  ],
  name: [
    { required: true, message: '请输入核心股东名称', trigger: 'blur' }
  ]
});
const formRef = ref(null);

// 获取核心股东列表
const getShareholderList = async () => {
  try {
    const response = await fetch('/api/core_shareholder/', {
      method: 'GET',
      credentials: 'include',
      headers: buildFetchApiHeaders({
        'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
      }),
    });
    const data = await response.json();
    if (data.status === 'ok') {
      shareholderList.value = data.data;
    } else {
      ElMessage.error(data.message);
    }
  } catch (error) {
    ElMessage.error(`数据查询失败：${error.message}`);
  }
};

// 打开新增弹窗
const openAddDialog = () => {
  isEdit.value = false;
  dialogTitle.value = '添加核心股东';
  resetForm();
  dialogVisible.value = true;
};

// 打开编辑弹窗
const openEditDialog = (row) => {
  isEdit.value = true;
  dialogTitle.value = '编辑核心股东';
  // 赋值表单数据
  formData.core_id = row.core_id;
  formData.name = row.name;
  formData.group_label = row.group_label || '';
  formData.is_front_shareholder = row.is_front_shareholder;
  formData.front_account = row.front_account || '';
  dialogVisible.value = true;
};

// 重置表单
const resetForm = () => {
  formData.core_id = '';
  formData.name = '';
  formData.group_label = '';
  formData.is_front_shareholder = false;
  formData.front_account = '';
  formRef.value?.resetFields();
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  await formRef.value.validate((valid) => {
    if (!valid) return;

    if (isEdit.value) {
      // 编辑提交
      updateShareholder();
    } else {
      // 新增提交
      addShareholder();
    }
  });
};

// 新增核心股东
const addShareholder = async () => {
  try {
    const response = await fetch('/api/core_shareholder/', {
      method: 'POST',
      credentials: 'include',
      headers: buildFetchApiHeaders({
        'Content-Type': 'application/json',
        'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
      }),
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (data.status === 'ok') {
      ElMessage.success(data.message);
      dialogVisible.value = false;
      getShareholderList();
    } else {
      ElMessage.error(data.message);
    }
  } catch (error) {
    ElMessage.error(`新增失败：${error.message}`);
  }
};

// 编辑核心股东
const updateShareholder = async () => {
  try {
    const response = await fetch(`/api/core_shareholder/${formData.core_id}/`, {
      method: 'PUT',
      credentials: 'include',
      headers: buildFetchApiHeaders({
        'Content-Type': 'application/json',
        'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
      }),
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (data.status === 'ok') {
      ElMessage.success(data.message);
      dialogVisible.value = false;
      getShareholderList();
    } else {
      ElMessage.error(data.message);
    }
  } catch (error) {
    ElMessage.error(`编辑失败：${error.message}`);
  }
};

// 删除单个核心股东
const handleDelete = async (core_id) => {
  try {
    const response = await fetch(`/api/core_shareholder/${core_id}/`, {
      method: 'DELETE',
      credentials: 'include',
      headers: buildFetchApiHeaders({
        'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
      }),
    });
    const data = await response.json();
    if (data.status === 'ok') {
      ElMessage.success(data.message);
      getShareholderList();
    } else {
      ElMessage.error(data.message);
    }
  } catch (error) {
    ElMessage.error(`删除失败：${error.message}`);
  }
};

// 批量删除
const handleBatchDelete = async () => {
  try {
    const response = await fetch('/api/core_shareholder/batch_delete/', {
      method: 'POST',
      credentials: 'include',
      headers: buildFetchApiHeaders({
        'Content-Type': 'application/json',
        'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
      }),
      body: JSON.stringify({ core_ids: selectedIds.value }),
    });
    const data = await response.json();
    if (data.status === 'ok') {
      ElMessage.success(data.message);
      getShareholderList();
      selectedIds.value = [];
    } else {
      ElMessage.error(data.message);
    }
  } catch (error) {
    ElMessage.error(`批量删除失败：${error.message}`);
  }
};

// 表格选择事件
const handleSelectionChange = (val) => {
  selectedIds.value = val.map(item => item.core_id);
};

// 页面加载时获取数据
onMounted(() => {
  getShareholderList();
});
</script>

<style scoped>
/* 和股东名册页面一致的容器样式，左对齐布局 */
.core-shareholder-container {
  padding: 20px;
}

.card-container {
  width: 100%;
}

.top-operation {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
}

.empty-data {
  padding: 40px 0;
  text-align: center;
}

.dialog-footer {
  text-align: right;
}
</style>